"use client";

import { cn } from "@/lib/utils/general-utils";
import React, { type ReactNode } from "react";

export type HeadingVariant =
  "display" | "title" | "section" | "subsection" | "detail";
export type HeadingSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
export type HeadingWeight = "normal" | "medium" | "semibold" | "bold";
export type HeadingColor =
  | "default"
  | "primary"
  | "secondary"
  | "muted"
  | "inverse"
  | "success"
  | "warning"
  | "danger";
export type SubheadingVariant = "small" | "medium" | "large";

export interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children?: ReactNode;
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  variant?: HeadingVariant;
  size?: HeadingSize;
  weight?: HeadingWeight;
  color?: HeadingColor;
  className?: string;
  loading?: boolean;
  /** @deprecated Animations removed. Prop kept for backward compat. */
  animate?: boolean;
  /** @deprecated Animations removed. Prop kept for backward compat. */
  animationDelay?: number;
  truncate?: boolean;
  centered?: boolean;
}

export interface SubheadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children?: ReactNode;
  level?: 2 | 3 | 4 | 5 | 6;
  variant?: SubheadingVariant;
  color?: HeadingColor;
  className?: string;
  loading?: boolean;
  /** @deprecated Animations removed. Prop kept for backward compat. */
  animate?: boolean;
  /** @deprecated Animations removed. Prop kept for backward compat. */
  animationDelay?: number;
  truncate?: boolean;
  centered?: boolean;
}

function getVariantDefaults(variant: HeadingVariant): {
  size: HeadingSize;
  weight: HeadingWeight;
} {
  switch (variant) {
    case "display":
      return { size: "3xl", weight: "bold" };
    case "title":
      return { size: "2xl", weight: "semibold" };
    case "section":
      return { size: "xl", weight: "semibold" };
    case "subsection":
      return { size: "lg", weight: "medium" };
    case "detail":
      return { size: "md", weight: "medium" };
    default:
      return { size: "md", weight: "medium" };
  }
}

function getSizeClasses(size: HeadingSize): string {
  switch (size) {
    case "xs":
      return "text-xs leading-4";
    case "sm":
      return "text-sm leading-5";
    case "md":
      return "text-base leading-6";
    case "lg":
      return "text-lg leading-7";
    case "xl":
      return "text-xl leading-7";
    case "2xl":
      return "text-2xl leading-8";
    case "3xl":
      return "text-3xl leading-9";
    default:
      return "text-base leading-6";
  }
}

function getWeightClasses(weight: HeadingWeight): string {
  switch (weight) {
    case "normal":
      return "font-normal";
    case "medium":
      return "font-medium";
    case "semibold":
      return "font-semibold";
    case "bold":
      return "font-bold";
    default:
      return "font-medium";
  }
}

function getColorClasses(color: HeadingColor): string {
  switch (color) {
    case "primary":
      return "text-blue-600";
    case "secondary":
      return "text-gray-600";
    case "muted":
      return "text-text-tertiary";
    case "inverse":
      return "text-white";
    case "success":
      return "text-status-success-text";
    case "warning":
      return "text-status-warning-text";
    case "danger":
      return "text-status-error-text";
    case "default":
    default:
      return "text-gray-900";
  }
}

function getSubheadingVariantClasses(variant: SubheadingVariant): string {
  switch (variant) {
    case "small":
      return "text-sm leading-5 font-medium";
    case "large":
      return "text-lg leading-7 font-medium";
    case "medium":
    default:
      return "text-base leading-6 font-medium";
  }
}

function HeadingSkeleton({
  size = "md",
  className,
  centered = false,
}: {
  size?: HeadingSize;
  className?: string;
  centered?: boolean;
}) {
  const sizeClass = getSizeClasses(size);
  const width =
    size === "3xl"
      ? "w-3/4"
      : size === "2xl"
        ? "w-2/3"
        : size === "xl"
          ? "w-1/2"
          : "w-1/3";

  return (
    <div
      className={cn(
        "animate-pulse bg-gray-200 rounded",
        sizeClass,
        width,
        centered && "mx-auto",
        className,
      )}
    />
  );
}

export function Heading({
  children,
  level = 1,
  variant = "title",
  size,
  weight,
  color = "default",
  className,
  loading = false,
  animate: _animate,
  animationDelay: _animationDelay,
  truncate = false,
  centered = false,
  ...props
}: HeadingProps) {
  const variantDefaults = getVariantDefaults(variant);
  const finalSize = size || variantDefaults.size;
  const finalWeight = weight || variantDefaults.weight;

  if (loading) {
    return (
      <HeadingSkeleton
        size={finalSize}
        className={className}
        centered={centered}
      />
    );
  }

  const headingProps = {
    className: cn(
      getSizeClasses(finalSize),
      getWeightClasses(finalWeight),
      getColorClasses(color),
      truncate && "truncate",
      centered && "text-center",
      className,
    ),
    ...props,
  };

  switch (level) {
    case 1:
      return <h1 {...headingProps}>{children}</h1>;
    case 2:
      return <h2 {...headingProps}>{children}</h2>;
    case 3:
      return <h3 {...headingProps}>{children}</h3>;
    case 4:
      return <h4 {...headingProps}>{children}</h4>;
    case 5:
      return <h5 {...headingProps}>{children}</h5>;
    case 6:
      return <h6 {...headingProps}>{children}</h6>;
    default:
      return <h1 {...headingProps}>{children}</h1>;
  }
}

export function Subheading({
  children,
  level = 2,
  variant = "medium",
  color = "secondary",
  className,
  loading = false,
  animate: _animate,
  animationDelay: _animationDelay,
  truncate = false,
  centered = false,
  ...props
}: SubheadingProps) {
  if (loading) {
    return (
      <HeadingSkeleton size="md" className={className} centered={centered} />
    );
  }

  const headingProps = {
    className: cn(
      getSubheadingVariantClasses(variant),
      getColorClasses(color),
      truncate && "truncate",
      centered && "text-center",
      className,
    ),
    ...props,
  };

  switch (level) {
    case 2:
      return <h2 {...headingProps}>{children}</h2>;
    case 3:
      return <h3 {...headingProps}>{children}</h3>;
    case 4:
      return <h4 {...headingProps}>{children}</h4>;
    case 5:
      return <h5 {...headingProps}>{children}</h5>;
    case 6:
      return <h6 {...headingProps}>{children}</h6>;
    default:
      return <h2 {...headingProps}>{children}</h2>;
  }
}
